import React, { useEffect, useMemo, useState } from "react";
import CommonFooter from "../../core/common/footer/commonFooter";
import Table from "../../core/pagination/datatable";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import { DatePicker } from "antd";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import Select from "react-select";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { getInvoiceReport, getCustomerOptions } from "../../api/invoiceApi";

const Invoicereportnew = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [summary, setSummary] = useState({
    totalAmount: 0,
    totalPaid: 0,
    totalUnpaid: 0,
    overdue: 0,
  });

  const [filters, setFilters] = useState({
    date: null,
    customer: null,
    status: null,
  });

  const statusOptions = [
    { value: "Paid", label: "Paid" },
    { value: "Unpaid", label: "Unpaid" },
    { value: "Overdue", label: "Overdue" },
  ];

  const formatCurrency = (value) => {
    const number = Number(value || 0);
    return `$${number.toLocaleString()}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return dayjs(date).format("DD MMM YYYY");
  };

  const fetchCustomerOptions = async () => {
    const options = await getCustomerOptions();
    setCustomerOptions(options);
  };

  const fetchInvoiceReport = async (customFilters = filters) => {
    try {
      setLoading(true);

      const params = {};

      if (customFilters?.status?.value) {
        params.status = customFilters.status.value;
      }

      if (customFilters?.customer?.value) {
        params.customer = customFilters.customer.value;
      }

      if (customFilters?.date) {
        params.date = dayjs(customFilters.date).format("YYYY-MM-DD");
      }

      const response = await getInvoiceReport(params);

      setSummary({
        totalAmount: response?.summary?.totalAmount || 0,
        totalPaid: response?.summary?.totalPaid || 0,
        totalUnpaid: response?.summary?.totalUnpaid || 0,
        overdue: response?.summary?.overdue || 0,
      });

      const rows = (response?.data || []).map((item) => ({
        ...item,
        key: item._id,
        invoiceNo: item.invoiceNo || "-",
        customer: item.customer || "N/A",
        dueDate: formatDate(item.dueDate),
        amount: formatCurrency(item.amount),
        paid: formatCurrency(item.paid),
        amountDue: formatCurrency(item.amountDue),
        rawStatus: item.status || "Unpaid",
      }));

      setReportData(rows);
    } catch (error) {
      console.error("Invoice report fetch error:", error);
      setReportData([]);
      setSummary({
        totalAmount: 0,
        totalPaid: 0,
        totalUnpaid: 0,
        overdue: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerOptions();
    fetchInvoiceReport();
  }, []);

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    fetchInvoiceReport(filters);
  };

  const handleRefresh = () => {
    const resetFilters = {
      date: null,
      customer: null,
      status: null,
    };
    setFilters(resetFilters);
    fetchInvoiceReport(resetFilters);
  };

  const columns = useMemo(
    () => [
      {
        title: "Invoice Number",
        dataIndex: "invoiceNo",
        render: (text) => (
          <Link to="#" className="text-primary">
            {text}
          </Link>
        ),
        sorter: (a, b) => (a.invoiceNo || "").localeCompare(b.invoiceNo || ""),
      },
      {
        title: "Customer",
        dataIndex: "customer",
        sorter: (a, b) => (a.customer || "").localeCompare(b.customer || ""),
      },
      {
        title: "Due Date",
        dataIndex: "dueDate",
        sorter: (a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""),
      },
      {
        title: "Amount",
        dataIndex: "amount",
        sorter: (a, b) => (a.amount || "").localeCompare(b.amount || ""),
      },
      {
        title: "Paid",
        dataIndex: "paid",
        sorter: (a, b) => (a.paid || "").localeCompare(b.paid || ""),
      },
      {
        title: "Amount Due",
        dataIndex: "amountDue",
        sorter: (a, b) => (a.amountDue || "").localeCompare(b.amountDue || ""),
      },
      {
        title: window.translate ? window.translate("Status") : "Status",
        dataIndex: "rawStatus",
        render: (text) => {
          const badgeClass =
            text === "Paid"
              ? "badge-success"
              : text === "Overdue"
              ? "badge-danger"
              : "badge-warning";

          return (
            <span
              className={`badge ${badgeClass} d-inline-flex align-items-center badge-xs`}
            >
              <i className="ti ti-point-filled me-1" />
              {text}
            </span>
          );
        },
        sorter: (a, b) => (a.rawStatus || "").localeCompare(b.rawStatus || ""),
      },
    ],
    []
  );

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Invoice Report</h4>
                <h6>Manage Your Invoice Report</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleRefresh();
                  }}
                >
                  <RefreshIcon />
                </a>
              </li>
              <CollapesIcon />
            </ul>
          </div>

          <div className="row">
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="card border border-success sale-widget flex-fill">
                <div className="card-body d-flex align-items-center">
                  <span className="sale-icon bg-success text-white">
                    <i className="ti ti-align-box-bottom-left-filled fs-24" />
                  </span>
                  <div className="ms-2">
                    <p className="fw-medium mb-1">Total Amount</p>
                    <div>
                      <h3>{formatCurrency(summary.totalAmount)}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="card border border-info sale-widget flex-fill">
                <div className="card-body d-flex align-items-center">
                  <span className="sale-icon bg-info text-white">
                    <i className="ti ti-align-box-bottom-left-filled fs-24" />
                  </span>
                  <div className="ms-2">
                    <p className="fw-medium mb-1">Total Paid</p>
                    <div>
                      <h3>{formatCurrency(summary.totalPaid)}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="card border border-orange sale-widget flex-fill">
                <div className="card-body d-flex align-items-center">
                  <span className="sale-icon bg-orange text-white">
                    <i className="ti ti-moneybag fs-24" />
                  </span>
                  <div className="ms-2">
                    <p className="fw-medium mb-1">Total Unpaid</p>
                    <div>
                      <h3>{formatCurrency(summary.totalUnpaid)}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="card border border-danger sale-widget flex-fill">
                <div className="card-body d-flex align-items-center">
                  <span className="sale-icon bg-danger text-white">
                    <i className="ti ti-alert-circle-filled fs-24" />
                  </span>
                  <div className="ms-2">
                    <p className="fw-medium mb-1">Overdue</p>
                    <div>
                      <h3>{formatCurrency(summary.overdue)}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0">
            <div className="card-body pb-1">
              <form onSubmit={handleGenerateReport}>
                <div className="row align-items-end">
                  <div className="col-lg-10">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label">Choose Date&nbsp;</label>
                          <div className="input-icon-start position-relative">
                            <DatePicker
                              className="form-control datetimepicker"
                              placeholder="dd/mm/yyyy"
                              value={filters.date}
                              onChange={(date) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  date,
                                }))
                              }
                            />
                            <span className="input-icon-left">
                              <i className="ti ti-calendar" />
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label">Customer</label>
                          <Select
                            classNamePrefix="react-select"
                            options={customerOptions}
                            placeholder="Choose"
                            value={filters.customer}
                            onChange={(selected) =>
                              setFilters((prev) => ({
                                ...prev,
                                customer: selected,
                              }))
                            }
                            isClearable
                          />
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <Select
                            classNamePrefix="react-select"
                            options={statusOptions}
                            placeholder="Choose"
                            value={filters.status}
                            onChange={(selected) =>
                              setFilters((prev) => ({
                                ...prev,
                                status: selected,
                              }))
                            }
                            isClearable
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-2">
                    <div className="mb-3">
                      <button className="btn btn-primary w-100" type="submit">
                        {loading ? "Loading..." : "Generate Report"}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="card table-list-card no-search">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div>
                <h4>Invoice Report</h4>
              </div>
              <ul className="table-top-head">
                <TooltipIcons />
                <li>
                  <a
                    href="#"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title="Print"
                    onClick={(e) => {
                      e.preventDefault();
                      window.print();
                    }}
                  >
                    <i className="ti ti-printer" />
                  </a>
                </li>
              </ul>
            </div>

            <div className="card-body">
              <div className="table-responsive">
                <Table columns={columns} dataSource={reportData} />
                {!loading && reportData.length === 0 && (
                  <div className="text-center py-4">No invoice report data found</div>
                )}
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>
    </div>
  );
};

export default Invoicereportnew;